import { getDecimalDisplay } from 'utils/priceUtils';

import { getFormattedMembershipDiscount } from './utils';

/**
 * Renders the activate membership pricing.
 * @param {Object} props
 * @param {number} props.annualPlusMembershipCost - The annual plus membership cost.
 * @param {Object} props.availableCredits - The available credits.
 * @param {number} props.availableCredits.credits - The available credits.
 * @param {number} props.availableCredits.creditsExpirationDays - The available credits expiration days.
 * @param {Object} props.availableTrialMembership - The available trial membership.
 * @param {number} props.availableTrialMembership.trialDurationDays - The available trial membership duration days.
 * @param {Object} props.newMemberOffer - The new member offer.
 * @param {string} props.newMemberOffer.type - The new member offer type.
 * @param {number} props.newMemberOffer.discountAmount - The new member offer discount amount.
 * @param {string} props.newMemberOffer.discountType - The new member offer discount type.
 * @param {number} props.newMemberOffer.discountPrice - The new member offer discount price.
 */
function ActivateMembershipPricing({
    annualPlusMembershipCost,
    availableCredits,
    availableTrialMembership,
    newMemberOffer,
}) {
    const formattedDiscount = getFormattedMembershipDiscount({ newMemberOffer });

    // TRIAL PRICING (Discounts and no offer states)
    if (availableTrialMembership && !availableCredits?.credits) {
        if (formattedDiscount) {
            return (
                <div className="justify-between gap-10 md:flex">
                    <p className="text-plus-membership font-grotesk-bold">
                        Limited-time offer:
                        <br />
                        Free {availableTrialMembership?.trialDurationDays}-day trial plus {formattedDiscount} off
                    </p>
                    <p>
                        <span className="font-grotesk-bold">
                            Only ${getDecimalDisplay(newMemberOffer.discountPrice)}{' '}
                            <span className="line-through">${annualPlusMembershipCost}</span> this year
                        </span>{' '}
                        | Billed annually
                    </p>
                </div>
            );
        }

        return (
            <div className="justify-between gap-10 md:flex">
                <span className="text-plus-membership font-grotesk-bold">
                    Free {availableTrialMembership?.trialDurationDays}-day trial
                </span>
                <p>Then ${annualPlusMembershipCost}/year</p>
            </div>
        );
    }

    // CREDITS PRICING (for Trial & Paid memberships)
    if (availableCredits?.credits > 0) {
        return (
            <div className="justify-between gap-10 md:flex">
                <p className="text-plus-membership font-grotesk-bold">
                    Limited-time offer:
                    <br />
                    Get ${availableCredits.credits} when you join, use within {availableCredits.creditsExpirationDays}{' '}
                    days
                </p>
                <p>
                    <span className="font-grotesk-bold">${annualPlusMembershipCost} per year</span> | Billed annually
                </p>
            </div>
        );
    }

    // DISCOUNT PRICING (for Paid memberships)
    if (formattedDiscount) {
        return (
            <div className="justify-between gap-10 md:flex">
                <p className="text-plus-membership font-grotesk-bold">For a limited time get {formattedDiscount} off</p>
                <p>
                    <span className="font-grotesk-bold">
                        Only ${getDecimalDisplay(newMemberOffer.discountPrice)}{' '}
                        <span className="line-through">${annualPlusMembershipCost}</span> this year
                    </span>{' '}
                    | Billed annually
                </p>
            </div>
        );
    }

    // DEFAULT PRICING (for Paid memberships)
    return (
        <div className="justify-between gap-10 md:flex">
            <p>
                <span className="font-grotesk-bold">${annualPlusMembershipCost} per year</span> | Billed annually
            </p>
            <p className="ml-auto text-grey">
                That&apos;s just ${getDecimalDisplay(annualPlusMembershipCost / 12)} per month!
            </p>
        </div>
    );
}

export { ActivateMembershipPricing };
